// src/pages/ClientsHome.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { List, Send, Users, Bell, ShieldCheck, CheckCircle2, Star, Search, Filter, BriefcaseBusiness, ArrowRight, Sparkles } from 'lucide-react'
import { DEFAULT_PROFESSION, ProfessionKey } from '../professions'
import { getCurrencyForCountry, normalizeCountryCode } from '../countryRules'
import { buildProviderLocation } from '../locationDisplay'

type SearchResult = {
  userId?: string
  name: string
  profession: string
  displayProfession?: string | null
  stack?: ProfessionKey | string | null
  specialty?: string | null
  title?: string | null
  specialties?: string[]
  profile?: string | null
  location?: string
  state?: string | null
  price?: string | null
  currency?: string | null
  countryCode?: string | null
  country?: string | null
  rating?: number | null
  ratingCount?: number | null
  image?: string | null
  slug: string
  availabilitySlots?: string[]
}

type StackShowcasePayload = {
  items: any[]
  qualifiedBookingCount: number
}

const SEARCH_PUBLIC_API = (
  import.meta.env.VITE_SEARCH_API_BASE ||
  import.meta.env.VITE_API_BASE ||
  'https://54uqjwvuke.execute-api.ap-southeast-2.amazonaws.com/prod'
).replace(/\/+$/, '')

const STACK_API_BASES: Record<ProfessionKey, string | undefined> = {
  doctor: import.meta.env.VITE_DOCTOR_API_BASE || import.meta.env.VITE_API_DOCTOR_BASE || import.meta.env.VITE_API_BASE,
  allied: import.meta.env.VITE_ALLIED_API_BASE || import.meta.env.VITE_API_ALLIED_BASE || import.meta.env.VITE_API_BASE,
  education: import.meta.env.VITE_EDUCATION_API_BASE || import.meta.env.VITE_API_EDUCATION_BASE || import.meta.env.VITE_API_BASE,
  financial: import.meta.env.VITE_FINANCIAL_API_BASE || import.meta.env.VITE_API_FINANCIAL_BASE || import.meta.env.VITE_API_BASE,
  migration: import.meta.env.VITE_MIGRATION_API_BASE || import.meta.env.VITE_API_MIGRATION_BASE || import.meta.env.VITE_API_BASE,
  legal: import.meta.env.VITE_LEGAL_API_BASE || import.meta.env.VITE_API_LEGAL_BASE || import.meta.env.VITE_API_BASE,
  property: import.meta.env.VITE_PROPERTY_API_BASE || import.meta.env.VITE_API_PROPERTY_BASE || import.meta.env.VITE_API_BASE,
  tech: import.meta.env.VITE_TECH_API_BASE || import.meta.env.VITE_API_TECH_BASE || import.meta.env.VITE_API_BASE,
}

const STACK_SHOWCASE_PATHS: Record<ProfessionKey, string> = {
  doctor: '/public/doctors/showcase',
  allied: '/public/allied/showcase',
  education: '/public/education/showcase',
  financial: '/public/financial/showcase',
  migration: '/public/migration/showcase',
  legal: '/public/legal/showcase',
  property: '/public/property/showcase',
  tech: '/public/tech/showcase',
}

const availabilityIndexByStack: Record<ProfessionKey, Map<string, string[]>> = {
  doctor: new Map(),
  allied: new Map(),
  education: new Map(),
  financial: new Map(),
  migration: new Map(),
  legal: new Map(),
  property: new Map(),
  tech: new Map(),
}
const availabilityIndexLoadedByStack: Record<ProfessionKey, boolean> = {
  doctor: false,
  allied: false,
  education: false,
  financial: false,
  migration: false,
  legal: false,
  property: false,
  tech: false,
}
const availabilityHydrationByStack: Partial<Record<ProfessionKey, Promise<StackShowcasePayload>>> = {}

let searchApiDisabledUntil = 0
const isSearchApiDisabled = () => searchApiDisabledUntil && Date.now() < searchApiDisabledUntil
const disableSearchApi = (ms = 2 * 60 * 1000) => {
  searchApiDisabledUntil = Date.now() + ms
}

const SEARCH_HINTS = ['Tax', 'Tutor', 'Lawyer', 'Dietitian', 'Music', 'Physio', 'Migration', 'Nurse', 'IELTS', 'Mortgage']

const useRotatingPlaceholder = (enabled: boolean, hints: string[], delay = 1000) => {
  const [idx, setIdx] = useState(0)
  useEffect(() => {
    if (!enabled || !hints.length) return undefined
    const t = setInterval(() => setIdx((i) => (i + 1) % hints.length), delay)
    return () => clearInterval(t)
  }, [enabled, hints, delay])
  return hints.length ? hints[idx] : ''
}

const renderStars = (val: any) => {
  const n = Math.max(1, Math.min(5, Math.round(Number(val) || 0)))
  return Array.from({ length: n }).map((_, i) => (
    <span key={i} className="star" aria-hidden>
      ★
    </span>
  ))
}

const reviewCountLabel = (value?: string | number | null) => {
  const count = Number(value) || 0
  return `${count.toLocaleString()} ${count === 1 ? 'review' : 'reviews'}`
}

const CONTACT_SLOT = 'REQUEST_BOOKING'

const parseReviewDate = (value?: string | null) => {
  if (!value) return null
  const trimmed = String(value).trim()
  if (!trimmed || trimmed === CONTACT_SLOT) return null
  if (/^\d+$/.test(trimmed)) {
    const raw = Number(trimmed)
    const ms = trimmed.length <= 10 ? raw * 1000 : raw
    const parsed = new Date(ms)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  const parsed = new Date(trimmed)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const formatReviewDate = (slotISO?: string | null, createdAt?: string | null) => {
  const parsed = parseReviewDate(slotISO) || parseReviewDate(createdAt)
  if (!parsed) return ''
  return parsed.toLocaleDateString('en-AU', { month: 'short', day: 'numeric', year: 'numeric' })
}

const normalizeProviderReply = (reply: any) => {
  if (!reply || typeof reply !== 'object') return null
  const comment = String(reply.comment || reply.replyComment || reply.message || '').trim()
  if (!comment) return null
  const createdAt = String(reply.createdAt || reply.updatedAt || '').trim() || null
  const updatedAt = String(reply.updatedAt || reply.createdAt || '').trim() || null
  const replierName = String(reply.replierName || reply.replier || '').trim() || null
  return {
    comment,
    createdAt,
    updatedAt,
    replierName,
  }
}

const normalizeReviewItem = (review: any) => {
  if (!review || typeof review !== 'object') return review
  return {
    ...review,
    providerReply: normalizeProviderReply(review.providerReply),
  }
}

const sortReviewsByDate = (reviews: any[]) =>
  [...reviews].sort((a, b) => {
    const aDate = parseReviewDate(a?.slotISO) || parseReviewDate(a?.createdAt)
    const bDate = parseReviewDate(b?.slotISO) || parseReviewDate(b?.createdAt)
    const aTime = aDate ? aDate.getTime() : 0
    const bTime = bDate ? bDate.getTime() : 0
    return bTime - aTime
  })

const REVIEWS_PAGE_SIZE = 10
const REVIEWS_FETCH_LIMIT = 50
const MAX_REVIEWS = 500

const STACK_LABELS: Record<ProfessionKey, string> = {
  doctor: 'Medical Doctors',
  allied: 'Allied Health',
  education: 'Education & Tutoring',
  financial: 'Financial',
  migration: 'Migration & Visa',
  legal: 'Legal, Governance & Compliance',
  property: 'Property, Design & Building',
  tech: 'Tech, Creative & Media',
}

const REGION_CODE_OPTIONS = [
  'ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA',
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
  'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT',
]
const REGION_CODE_SET = new Set(REGION_CODE_OPTIONS)
const normalizeRegionToken = (value: unknown) =>
  String(value || '')
    .trim()
    .replace(/\.+$/, '')
    .toUpperCase()

const resolveRegion = (item: SearchResult): string => {
  const direct = String((item as any).region || (item as any).province || item.state || '').trim()
  if (direct) return direct
  const loc = String(item.location || '').trim()
  if (!loc) return ''
  const parts = loc.split(',').map((part) => part.trim()).filter(Boolean)
  for (const part of parts) {
    const token = normalizeRegionToken(part)
    if (REGION_CODE_SET.has(token)) return token
  }
  const tokenMatches = loc.toUpperCase().match(/\b[A-Z]{2,3}\b/g) || []
  for (const token of tokenMatches) {
    const normalized = normalizeRegionToken(token)
    if (REGION_CODE_SET.has(normalized)) return normalized
  }
  if (parts.length >= 2) return parts[parts.length - 2]
  return ''
}

const STACK_OPTIONS: Array<{ value: '' | ProfessionKey; label: string }> = [
  { value: '', label: 'All professions' },
  ...Object.entries(STACK_LABELS).map(([value, label]) => ({ value: value as ProfessionKey, label })),
]

const MAX_SHOWCASE_CARDS = 8
const LICENSE_VERIFIED_TOOLTIP =
  'License Verified: This professional’s license has been verified by EUCA Match.'

const providerFirstName = (name?: string | null) => {
  if (!name) return 'their'
  const first = name.trim().split(/\s+/)[0]
  return first || 'their'
}

const shouldHideAustraliaWide = (item: SearchResult) => {
  const skipNames = ['leopold chung']
  const name = (item?.name || '').toLowerCase()
  if (skipNames.some((n) => name.includes(n))) return true
  const tokens = [
    item?.profession,
    item?.specialty,
    item?.displayProfession,
    item?.title,
    ...(Array.isArray(item?.specialties) ? item.specialties : []),
  ]
    .filter(Boolean)
    .map((val) => String(val).toLowerCase())
  const isPrincipalLawyer = tokens.some((val) => val.includes('principal lawyer'))
  if (item?.stack === 'legal' || (item?.profession || '').toLowerCase().includes('legal')) {
    return isPrincipalLawyer
  }
  const specs = Array.isArray(item?.specialties) ? item.specialties : []
  const lowerSpecs = specs.map((s: string) => s.toLowerCase())
  const hasRegisteredNurse = lowerSpecs.some((s) => s.includes('registered nurse (ahpra)'))
  const hasDiabetesEducator = lowerSpecs.some((s) => s.includes('diabetes educator (adea)'))
  if (hasRegisteredNurse && !hasDiabetesEducator) return true
  return false
}

const guessStack = (item: SearchResult): ProfessionKey => {
  if (item.stack && STACK_LABELS[item.stack as ProfessionKey]) return item.stack as ProfessionKey
  const name = (item.profession || '').toLowerCase()
  if (name.includes('doctor')) return 'doctor'
  if (name.includes('allied')) return 'allied'
  if (name.includes('visa') || name.includes('migration')) return 'migration'
  if (name.includes('legal') || name.includes('law')) return 'legal'
  if (name.includes('property') || name.includes('real')) return 'property'
  if (name.includes('tech') || name.includes('creative') || name.includes('it')) return 'tech'
  if (name.includes('finance') || name.includes('account')) return 'financial'
  if (name.includes('educat') || name.includes('tutor')) return 'education'
  return DEFAULT_PROFESSION
}

const normalizeBadgeToken = (value: string) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

const hasLicenseVerifiedBadge = (item: SearchResult) => {
  const stack = guessStack(item)
  if (stack === 'doctor' || stack === 'allied' || stack === 'financial' || stack === 'migration') return true
  const tokens = [
    item.profession,
    item.displayProfession,
    item.specialty,
    item.title,
    ...(Array.isArray(item.specialties) ? item.specialties : []),
  ]
    .filter(Boolean)
    .map((value) => normalizeBadgeToken(String(value)))
    .filter(Boolean)
  if (stack === 'legal') {
    if (tokens.some((token) => token.includes('principal lawyer'))) return true
    if (tokens.some((token) => token.includes('nationally accredited mediator'))) return true
    if (tokens.some((token) => token.includes('naati') && token.includes('translator'))) return true
    return false
  }
  if (stack === 'property') {
    if (tokens.some((token) => token.includes('forensic engineer'))) return true
    if (tokens.some((token) => token.includes('real estate agent'))) return true
    if (tokens.some((token) => /\barchitect\b/.test(token))) return true
    return false
  }
  if (stack === 'education') {
    const labelTokens = [
      item.profession,
      item.displayProfession,
      item.specialty,
      item.title,
    ]
      .filter(Boolean)
      .map((value) => normalizeBadgeToken(String(value)))
      .filter(Boolean)
    if (labelTokens.some((token) => token.includes('education advisor'))) return true
    if (labelTokens.some((token) => /\blecturer\b/.test(token))) return true
    if (labelTokens.some((token) => /\bteacher\b/.test(token))) return true
    return false
  }
  return false
}

const hasAvailabilityListed = (item: SearchResult) =>
  (Array.isArray(item.availabilitySlots) && item.availabilitySlots.length > 0) ||
  Array.isArray((item as any)?.availability) && (item as any).availability.length > 0

const normalizeAvailabilitySlots = (value: any): string[] => {
  if (!Array.isArray(value)) return []
  return value
    .map((slot) => String(slot || '').trim())
    .filter(Boolean)
}

const rememberAvailabilityFromStackItems = (stack: ProfessionKey, items: any[]) => {
  availabilityIndexLoadedByStack[stack] = true
  const bucket = availabilityIndexByStack[stack]
  items.forEach((item) => {
    const userId = String(item?.userId || '').trim()
    if (!userId) return
    const slots = normalizeAvailabilitySlots(item?.availabilitySlots || item?.availability)
    if (!slots.length) return
    bucket.set(userId, slots)
  })
}

const resolveSearchStack = (item: SearchResult, preferred?: ProfessionKey | ''): ProfessionKey => {
  const explicit = String(item.stack || '').toLowerCase() as ProfessionKey
  if (STACK_SHOWCASE_PATHS[explicit]) return explicit
  if (preferred && STACK_SHOWCASE_PATHS[preferred]) return preferred
  return guessStack(item)
}

const applyAvailabilityFromIndex = (item: SearchResult, preferred?: ProfessionKey | ''): SearchResult => {
  if (hasAvailabilityListed(item)) return item
  const userId = String(item.userId || '').trim()
  if (!userId) return item
  const stack = resolveSearchStack(item, preferred)
  const slots = availabilityIndexByStack[stack].get(userId) || []
  if (!slots.length) return item
  return { ...item, availabilitySlots: [...slots] }
}

const LicenseVerifiedBadge = ({ item }: { item: SearchResult }) => {
  if (!hasLicenseVerifiedBadge(item)) return null
  return (
    <span
      className="license-verified-badge"
      aria-label="License Verified"
      role="img"
      tabIndex={0}
      data-tooltip={LICENSE_VERIFIED_TOOLTIP}
    >
      <ShieldCheck size={13} aria-hidden />
    </span>
  )
}

const formatPriceLabel = (pricing: any, fallbackCurrency: string) => {
  if (!pricing || typeof pricing !== 'object') return null
  const min = typeof pricing.minAud === 'number' ? pricing.minAud : typeof pricing.min === 'number' ? pricing.min : null
  const max = typeof pricing.maxAud === 'number' ? pricing.maxAud : typeof pricing.max === 'number' ? pricing.max : null
  const currencyCode =
    typeof pricing.currency === 'string' && pricing.currency.trim()
      ? pricing.currency.trim().toUpperCase()
      : String(fallbackCurrency || 'AUD').toUpperCase()
  if (min === 0) return 'Contact for quote'
  if (min != null && max != null && max >= min) return `$${min.toFixed(0)}–$${max.toFixed(0)} ${currencyCode}`
  if (min != null) return `From $${min.toFixed(0)} ${currencyCode}`
  return null
}

const mapSearchItem = (item: any): SearchResult => {
  const toSlug = (val: string) => (val || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
  const fallbackSlug = toSlug(item.name || item.userId || 'profile') || 'profile'
  const fallbackCurrency =
    (typeof item.currency === 'string' && item.currency.trim() ? item.currency.trim().toUpperCase() : '') ||
    getCurrencyForCountry(normalizeCountryCode(item.countryCode || item.country || 'AU'))
  const locationData = buildProviderLocation(item)
  const displayProfession = item.displayProfession || null
  const rating =
    typeof item.rating === 'number'
      ? Number(item.rating)
      : typeof item.rating === 'string'
        ? Number(item.rating)
        : typeof item.ratingAverage === 'number'
          ? Number(item.ratingAverage)
          : typeof item.ratingAverage === 'string'
            ? Number(item.ratingAverage)
            : null
  const ratingCount =
    typeof item.ratingCount === 'number'
      ? item.ratingCount
      : typeof item.ratingCount === 'string'
        ? Number(item.ratingCount)
        : typeof item.reviewCount === 'number'
          ? item.reviewCount
          : null
  const priceLabel =
    item.priceLabel ||
    formatPriceLabel(item.pricing, fallbackCurrency) ||
    null
  const image =
    item.image ||
    item.photoPublicUrl ||
    item.photoUrl ||
    item.photo ||
    null
  return {
    userId: item.userId,
    name: item.name || item.profession || 'Professional',
    profession: displayProfession || item.profession || 'Professional',
    displayProfession,
    stack: item.stack || null,
    specialty: item.specialty || null,
    title: item.title || null,
    specialties: Array.isArray(item.specialties) ? item.specialties : [],
    profile: item.profile || null,
    location: locationData.display || undefined,
    state: locationData.state || null,
    price: priceLabel,
    currency: item.currency || fallbackCurrency || null,
    countryCode: locationData.countryCode,
    country: locationData.country,
    rating: Number.isFinite(rating) ? rating : null,
    ratingCount,
    image,
    slug: item.slug || fallbackSlug,
    availabilitySlots: Array.isArray(item.availabilitySlots)
      ? item.availabilitySlots
      : Array.isArray(item.availability)
        ? item.availability
        : [],
  }
}

const orderResults = (items: SearchResult[]) => {
  const hasReviews = (item: SearchResult) => (Number(item.ratingCount) || 0) > 0

  const shuffle = (list: SearchResult[]) => {
    const copy = [...list]
    for (let i = copy.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[copy[i], copy[j]] = [copy[j], copy[i]]
    }
    return copy
  }

  const rated = shuffle(items.filter(hasReviews))
  const unrated = items.filter((i) => !hasReviews(i))
  const orderByAvailability = (list: SearchResult[]) => {
    const withAvailability = shuffle(list.filter((item) => hasAvailabilityListed(item)))
    const withoutAvailability = shuffle(list.filter((item) => !hasAvailabilityListed(item)))
    return [...withAvailability, ...withoutAvailability]
  }
  const unratedVerified = unrated.filter((item) => hasLicenseVerifiedBadge(item))
  const unratedOther = unrated.filter((item) => !hasLicenseVerifiedBadge(item))
  const orderedUnrated = [...orderByAvailability(unratedVerified), ...orderByAvailability(unratedOther)]

  return [...rated, ...orderedUnrated]
}

const fetchWithRetry = async (makeUrl: () => string, attempts = 3, delayMs = 250) => {
  let lastErr: any
  for (let i = 0; i < attempts; i++) {
    try {
      const res = await fetch(makeUrl(), { cache: 'no-store' })
      if (res.ok) return await res.json().catch(() => ({}))
      lastErr = new Error(res.statusText || 'Request failed')
    } catch (e) {
      lastErr = e
    }
    if (i < attempts - 1) await new Promise((r) => setTimeout(r, delayMs * (i + 1)))
  }
  throw lastErr || new Error('Request failed')
}

const fetchStackShowcase = async (profession: ProfessionKey): Promise<StackShowcasePayload> => {
  const apiBase = STACK_API_BASES[profession]
  const path = STACK_SHOWCASE_PATHS[profession]
  if (!apiBase || !path) return { items: [], qualifiedBookingCount: 0 }
  try {
    const makeUrl = () => {
      const url = new URL(`${apiBase}${path}`)
      url.searchParams.set('ts', Date.now().toString())
      return url.toString()
    }
    const json = await fetchWithRetry(makeUrl, 3, 300)
    const items = Array.isArray((json as any)?.items) ? (json as any).items : []
    rememberAvailabilityFromStackItems(profession, items)
    const qualifiedBookingCount = Math.max(0, Number((json as any)?.stackMetrics?.qualifiedBookingCount) || 0)
    return {
      items: items.map((item: any) => ({ ...item, stack: profession })),
      qualifiedBookingCount,
    }
  } catch {
    return { items: [], qualifiedBookingCount: 0 }
  }
}

const fetchStackShowcaseResults = async (profession: ProfessionKey) => {
  const payload = await fetchStackShowcase(profession)
  return {
    items: orderResults(payload.items.map(mapSearchItem)),
    qualifiedBookingCount: payload.qualifiedBookingCount,
  }
}

const hydrateAvailabilityForStack = async (stack: ProfessionKey) => {
  if (availabilityIndexLoadedByStack[stack]) return
  if (!availabilityHydrationByStack[stack]) {
    availabilityHydrationByStack[stack] = fetchStackShowcase(stack).finally(() => {
      delete availabilityHydrationByStack[stack]
    })
  }
  await availabilityHydrationByStack[stack]
}

const fetchSearchResults = async (q: string, profession: string) => {
  const stackKey = (profession || '').toLowerCase() as ProfessionKey
  let items: any[] = []
  if (SEARCH_PUBLIC_API && !isSearchApiDisabled()) {
    const url = () => {
      const u = new URL(`${SEARCH_PUBLIC_API}/public/search`)
      if (q.trim()) u.searchParams.set('q', q.trim())
      if (profession) u.searchParams.set('profession', profession)
      return u.toString()
    }
    try {
      const json = await fetchWithRetry(url, 3, 300)
      items = Array.isArray((json as any)?.items) ? (json as any).items : []
    } catch {
      disableSearchApi()
    }
  }
  if (!items.length && stackKey && STACK_SHOWCASE_PATHS[stackKey]) {
    const fallback = await fetchStackShowcase(stackKey)
    items = fallback.items
  }
  let mapped = items.map(mapSearchItem)
  const stacksToHydrate = new Set<ProfessionKey>()
  mapped.forEach((item) => {
    if (hasAvailabilityListed(item) || !item.userId) return
    const stack = resolveSearchStack(item, stackKey)
    if (!availabilityIndexLoadedByStack[stack]) stacksToHydrate.add(stack)
  })
  if (stacksToHydrate.size) {
    await Promise.all(Array.from(stacksToHydrate).map((stack) => hydrateAvailabilityForStack(stack)))
  }
  mapped = mapped.map((item) => applyAvailabilityFromIndex(item, stackKey))
  return orderResults(mapped)
}

const fetchSuggestions = async (q: string) => {
  if (!SEARCH_PUBLIC_API || isSearchApiDisabled()) return []
  const makeUrl = () => {
    const url = new URL(`${SEARCH_PUBLIC_API}/public/suggest`)
    if (q.trim()) url.searchParams.set('q', q.trim())
    return url.toString()
  }
  try {
    const json = await fetchWithRetry(makeUrl, 2, 200)
    return Array.isArray((json as any)?.suggestions) ? (json as any).suggestions : []
  } catch {
    disableSearchApi()
    return []
  }
}

export default function ClientsHome() {
  const heroLogo = '/logo.png'
  const [searchParams, setSearchParams] = useSearchParams()
  const [query, setQuery] = useState('')
  const [profession, setProfession] = useState<ProfessionKey | ''>('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [roleFilter, setRoleFilter] = useState('')
  const [stateFilter, setStateFilter] = useState('')
  const [ratingFilter, setRatingFilter] = useState('any')
  const [sortBy, setSortBy] = useState('recommended')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [suggestionsOpen, setSuggestionsOpen] = useState(false)
  const [suggestionsLoading, setSuggestionsLoading] = useState(false)
  const [showProfessionSelect, setShowProfessionSelect] = useState(true)
  const [searchFocused, setSearchFocused] = useState(false)
  const [showcase, setShowcase] = useState<Record<ProfessionKey, SearchResult[]>>({
    doctor: [],
    allied: [],
    education: [],
    financial: [],
    migration: [],
    legal: [],
    property: [],
    tech: [],
  })
  const [showcaseBookingCounts, setShowcaseBookingCounts] = useState<Record<ProfessionKey, number>>({
    doctor: 0,
    allied: 0,
    education: 0,
    financial: 0,
    migration: 0,
    legal: 0,
    property: 0,
    tech: 0,
  })
  const [showcaseLoading, setShowcaseLoading] = useState(false)
  const [showcaseError, setShowcaseError] = useState<string | null>(null)
  const showcaseRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const [reviewsPanel, setReviewsPanel] = useState<{
    provider: SearchResult | null
    reviews: any[]
    summary: any
    loading: boolean
    loadingMore: boolean
    nextCursor: string | null
    error: string | null
  }>({ provider: null, reviews: [], summary: null, loading: false, loadingMore: false, nextCursor: null, error: null })
  const [reviewsVisibleCount, setReviewsVisibleCount] = useState(REVIEWS_PAGE_SIZE)
  const reviewsScrollRef = useRef<HTMLDivElement | null>(null)
  const reviewsSentinelRef = useRef<HTMLDivElement | null>(null)
  const lastSearchKey = useRef('')
  const navigate = useNavigate()
  const resolveStackKey = (item: SearchResult | null): ProfessionKey | null => {
    if (!item) return null
    const explicit = item.stack && STACK_API_BASES[item.stack as ProfessionKey] ? (item.stack as ProfessionKey) : null
    if (explicit) return explicit
    const inferred = guessStack(item)
    return STACK_API_BASES[inferred] ? inferred : null
  }
  const resolveStackCandidates = (item: SearchResult | null): ProfessionKey[] => {
    if (!item) return []
    const out: ProfessionKey[] = []
    const seen = new Set<ProfessionKey>()
    const add = (key: ProfessionKey | null) => {
      if (!key || seen.has(key) || !STACK_API_BASES[key]) return
      seen.add(key)
      out.push(key)
    }
    const explicit = item.stack && STACK_API_BASES[item.stack as ProfessionKey] ? (item.stack as ProfessionKey) : null
    add(explicit)
    add(guessStack(item))
    ;(Object.keys(STACK_LABELS) as ProfessionKey[]).forEach((key) => add(key))
    return out
  }

  const fetchAllReviews = useCallback(
    async (item: SearchResult) => {
      const preferredStack = resolveStackKey(item)
      const stackCandidates = resolveStackCandidates(item)
      if (!stackCandidates.length || !item.userId) {
        setReviewsPanel((prev) => ({ ...prev, loading: false, loadingMore: false, error: 'Reviews unavailable for this provider.' }))
        return
      }
      try {
        let best: { reviews: any[]; summary: any; score: number } | null = null
        let lastError: any = null

        for (const stackKey of stackCandidates) {
          const apiBase = STACK_API_BASES[stackKey]
          if (!apiBase) continue
          try {
            let cursor: string | null = null
            let summary: any = null
            let all: any[] = []
            let pages = 0
            do {
              const qs = new URLSearchParams({ ts: Date.now().toString(), limit: String(REVIEWS_FETCH_LIMIT) })
              if (cursor) qs.set('cursor', cursor)
              const res = await fetch(`${apiBase}/reviews/providers/${item.userId}?${qs.toString()}`, { cache: 'no-store' })
              const json = await res.json().catch(() => ({}))
              if (!res.ok) throw new Error(json?.error || 'Failed to load reviews')
              const incoming = Array.isArray(json.reviews) ? json.reviews.map(normalizeReviewItem) : []
              all = all.concat(incoming)
              summary = json.summary || summary
              cursor = json.nextCursor || null
              pages += 1
            } while (cursor && all.length < MAX_REVIEWS && pages < 20)

            const sorted = sortReviewsByDate(all)
            const hasReplies = sorted.some((rev) => Boolean(rev?.providerReply?.comment))
            const ratingCount = Number(summary?.ratingCount || 0)
            const score =
              (hasReplies ? 1000000 : 0) +
              (stackKey === preferredStack ? 10000 : 0) +
              ratingCount * 10 +
              sorted.length
            if (!best || score > best.score) {
              best = { reviews: sorted, summary, score }
            }
            if (hasReplies && sorted.length) break
          } catch (err: any) {
            lastError = err
          }
        }

        if (!best) throw lastError || new Error('Failed to load reviews')

        setReviewsPanel((prev) => ({
          ...prev,
          loading: false,
          loadingMore: false,
          reviews: best.reviews,
          summary: best.summary,
          nextCursor: null,
          error: null,
        }))
        setReviewsVisibleCount(Math.min(REVIEWS_PAGE_SIZE, best.reviews.length))
      } catch (e: any) {
        setReviewsPanel((prev) => ({
          ...prev,
          loading: false,
          loadingMore: false,
          error: e?.message || 'Failed to load reviews',
        }))
      }
    },
    []
  )

  const openReviews = (item: SearchResult) => {
    setReviewsPanel({ provider: item, reviews: [], summary: null, loading: true, loadingMore: false, nextCursor: null, error: null })
    setReviewsVisibleCount(0)
    fetchAllReviews(item)
  }

  const closeReviews = () => {
    setReviewsPanel({ provider: null, reviews: [], summary: null, loading: false, loadingMore: false, nextCursor: null, error: null })
  }

  const loadMoreReviews = () => {
    if (reviewsVisibleCount >= reviewsPanel.reviews.length) return
    setReviewsVisibleCount((count) => Math.min(count + REVIEWS_PAGE_SIZE, reviewsPanel.reviews.length))
  }

  useEffect(() => {
    if (!reviewsPanel.provider) return
    if (reviewsVisibleCount >= reviewsPanel.reviews.length) return
    const root = reviewsScrollRef.current
    const target = reviewsSentinelRef.current
    if (!root || !target) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMoreReviews()
      },
      { root, rootMargin: '0px 0px 220px 0px', threshold: 0 }
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [reviewsPanel.provider, reviewsPanel.reviews.length, reviewsVisibleCount, loadMoreReviews])

  useEffect(() => {
    if (reviewsPanel.provider) {
      document.body.classList.add('reviews-no-scroll')
    } else {
      document.body.classList.remove('reviews-no-scroll')
    }
    return () => document.body.classList.remove('reviews-no-scroll')
  }, [reviewsPanel.provider])

  const rotatingPlaceholder = useRotatingPlaceholder(!searchFocused && !query, SEARCH_HINTS)

  const roleOptions = useMemo(() => {
    const stackLabels = new Set(Object.values(STACK_LABELS).map((label) => label.toLowerCase()))
    const seen = new Map<string, string>()
    results.forEach((pro) => {
      const label = String(pro.profession || '').trim()
      if (!label) return
      if (stackLabels.has(label.toLowerCase())) return
      const key = label.toLowerCase()
      if (!seen.has(key)) seen.set(key, label)
    })
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b))
  }, [results])
  const regionOptions = useMemo(() => {
    const seen = new Map<string, string>()
    results.forEach((item) => {
      const region = resolveRegion(item)
      if (!region) return
      const key = normalizeRegionToken(region)
      if (!key || seen.has(key)) return
      seen.set(key, region)
    })
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b))
  }, [results])
  const filtersActive = roleFilter || stateFilter || ratingFilter !== 'any'
  const activeFilterCount = [
    roleFilter,
    stateFilter,
    ratingFilter !== 'any' ? 'rating' : '',
  ].filter(Boolean).length
  const showFilters = Boolean(query.trim() || profession || results.length || filtersActive)

  useEffect(() => {
    if (roleFilter && !roleOptions.includes(roleFilter)) setRoleFilter('')
  }, [roleFilter, roleOptions])

  useEffect(() => {
    if (!stateFilter) return
    const target = normalizeRegionToken(stateFilter)
    const exists = regionOptions.some((region) => normalizeRegionToken(region) === target)
    if (!exists) setStateFilter('')
  }, [stateFilter, regionOptions])

  const clearFilters = () => {
    setRoleFilter('')
    setStateFilter('')
    setRatingFilter('any')
  }

  useEffect(() => {
    if (!query.trim() || query.trim().length < 2) {
      setSuggestions([])
      setSuggestionsOpen(false)
      return
    }
    let cancelled = false
    const t = setTimeout(async () => {
      setSuggestionsLoading(true)
      try {
        const sugg = await fetchSuggestions(query.trim())
        if (!cancelled) {
          setSuggestions(sugg)
          setSuggestionsOpen(searchFocused && sugg.length > 0)
        }
      } finally {
        if (!cancelled) setSuggestionsLoading(false)
      }
    }, 200)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [query, searchFocused])

  useEffect(() => {
    const qParam = searchParams.get('q') || ''
    const pParam = (searchParams.get('profession') as ProfessionKey | '') || ''
    const key = `${qParam.trim()}||${pParam}`
    if (!qParam.trim() && !pParam) {
      if (lastSearchKey.current !== '') {
        setResults([])
        setError(null)
        lastSearchKey.current = ''
      }
      setQuery('')
      setProfession('')
      return
    }
    if (key === lastSearchKey.current) return
    setQuery(qParam)
    setProfession(pParam)
    lastSearchKey.current = key
    runSearch(qParam, pParam, { push: false })
  }, [searchParams])

  const runSearch = async (val = query, prof = profession, opts: { push?: boolean } = {}) => {
    const push = opts.push !== false
    setSearchFocused(false)
    setLoading(true)
    setError(null)
    setSuggestionsOpen(false)
    if (push) {
      const params = new URLSearchParams()
      const trimmed = val.trim()
      if (trimmed) params.set('q', trimmed)
      if (prof) params.set('profession', prof)
      setSearchParams(params, { replace: false })
      lastSearchKey.current = `${trimmed}||${prof}`
    }
    try {
      const items = await fetchSearchResults(val, prof)
      setResults(items)
      if (!items.length) setError('No professionals found. Try a different keyword or profession.')
    } catch (e: any) {
      setError(e?.message || 'Search failed')
    } finally {
      setLoading(false)
    }
  }

  const filteredResults = useMemo(() => {
    const q = query.trim().toLowerCase()
    const role = roleFilter.toLowerCase().trim()
    const stateTarget = normalizeRegionToken(stateFilter)
    const minRating = ratingFilter === 'any' ? null : Number(ratingFilter)
    const randomTieBreakers = new Map<string, number>()

    const randomOrderValue = (item: SearchResult) => {
      const key = `${item.userId || ''}|${item.slug || ''}|${item.name || ''}|${item.profession || ''}`
      if (!randomTieBreakers.has(key)) randomTieBreakers.set(key, Math.random())
      return randomTieBreakers.get(key) || 0
    }

    const matchText = (r: SearchResult) => {
      if (!q) return true
      const haystack = [
        r.name || '',
        r.profession || '',
        r.profile || '',
        ...(Array.isArray(r.specialties) ? r.specialties : []),
      ]
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    }
    const matchRole = (r: SearchResult) => {
      if (!role) return true
      return String(r.profession || '').toLowerCase().trim() === role
    }
    const matchState = (r: SearchResult) => {
      if (!stateTarget) return true
      return normalizeRegionToken(resolveRegion(r)) === stateTarget
    }
    const matchRating = (r: SearchResult) => {
      if (!minRating) return true
      const ratingVal = Number(r.rating)
      return Number.isFinite(ratingVal) && ratingVal >= minRating
    }
    const byRecommended = (a: SearchResult, b: SearchResult) => {
      const ra = Number(a.ratingCount) || 0
      const rb = Number(b.ratingCount) || 0
      const aReviewed = ra > 0
      const bReviewed = rb > 0
      if (aReviewed !== bReviewed) return Number(bReviewed) - Number(aReviewed)
      if (aReviewed && bReviewed) {
        return randomOrderValue(a) - randomOrderValue(b)
      }
      if (!aReviewed && !bReviewed) {
        const av = hasLicenseVerifiedBadge(a) ? 1 : 0
        const bv = hasLicenseVerifiedBadge(b) ? 1 : 0
        if (bv !== av) return bv - av
        const aa = hasAvailabilityListed(a) ? 1 : 0
        const ba = hasAvailabilityListed(b) ? 1 : 0
        if (ba !== aa) return ba - aa
        return randomOrderValue(a) - randomOrderValue(b)
      }
      return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
    }
    const byHighestRated = (a: SearchResult, b: SearchResult) => {
      const ra = Number(a.rating) || 0
      const rb = Number(b.rating) || 0
      if (rb !== ra) return rb - ra
      return (b.ratingCount || 0) - (a.ratingCount || 0)
    }
    const byMostReviews = (a: SearchResult, b: SearchResult) => {
      const ra = Number(a.ratingCount) || 0
      const rb = Number(b.ratingCount) || 0
      if (rb !== ra) return rb - ra
      if (ra <= 0 && rb <= 0) {
        const av = hasLicenseVerifiedBadge(a) ? 1 : 0
        const bv = hasLicenseVerifiedBadge(b) ? 1 : 0
        if (bv !== av) return bv - av
        const aa = hasAvailabilityListed(a) ? 1 : 0
        const ba = hasAvailabilityListed(b) ? 1 : 0
        if (ba !== aa) return ba - aa
        return randomOrderValue(a) - randomOrderValue(b)
      }
      const byRating = (Number(b.rating) || 0) - (Number(a.rating) || 0)
      if (byRating !== 0) return byRating
      return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
    }
    const byNameAsc = (a: SearchResult, b: SearchResult) =>
      String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
    const byNameDesc = (a: SearchResult, b: SearchResult) =>
      String(b.name || '').localeCompare(String(a.name || ''), undefined, { sensitivity: 'base' })

    const matched = results.filter(
      (r) =>
        matchText(r) &&
        matchRole(r) &&
        matchState(r) &&
        matchRating(r)
    )
    const sorted = [...matched]
    if (sortBy === 'rating') sorted.sort(byHighestRated)
    else if (sortBy === 'reviews') sorted.sort(byMostReviews)
    else if (sortBy === 'name-asc') sorted.sort(byNameAsc)
    else if (sortBy === 'name-desc') sorted.sort(byNameDesc)
    else sorted.sort(byRecommended)
    return sorted
  }, [results, query, roleFilter, stateFilter, ratingFilter, sortBy])

  const directBookingUrl = (item: SearchResult) => {
    const stack = guessStack(item)
    const professionLabel =
      item.displayProfession ||
      item.profession ||
      STACK_LABELS[stack] ||
      'Professional'
    const safeSlug = (item.slug || item.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '')
    const params = new URLSearchParams({
      specialty: 'Direct booking',
      providerId: item.userId || item.slug,
      providerSlug: safeSlug,
      providerName: item.name,
    })
    if (item.image) params.set('providerImage', item.image)
    return `/results/${stack}?${params.toString()}`
  }

  useEffect(() => {
    const loadShowcase = async () => {
      try {
        setShowcaseLoading(true)
        setShowcaseError(null)
        const stacks: ProfessionKey[] = ['doctor', 'allied', 'education', 'financial', 'migration', 'legal', 'property', 'tech']
        const results = await Promise.all(
          stacks.map(async (stack) => {
            const data = await fetchStackShowcaseResults(stack)
            return {
              stack,
              items: data.items,
              qualifiedBookingCount: data.qualifiedBookingCount,
            }
          })
        )
        const map: Record<ProfessionKey, SearchResult[]> = {
          doctor: [],
          allied: [],
          education: [],
          financial: [],
          migration: [],
          legal: [],
          property: [],
          tech: [],
        }
        const bookingCounts: Record<ProfessionKey, number> = {
          doctor: 0,
          allied: 0,
          education: 0,
          financial: 0,
          migration: 0,
          legal: 0,
          property: 0,
          tech: 0,
        }
        results.forEach(({ stack, items, qualifiedBookingCount }) => {
          map[stack] = items
          bookingCounts[stack] = qualifiedBookingCount
        })
        setShowcase(map)
        setShowcaseBookingCounts(bookingCounts)
      } catch (e: any) {
        setShowcaseError(e?.message || 'Failed to load professionals')
      } finally {
        setShowcaseLoading(false)
      }
    }
    loadShowcase()
  }, [])

  // Warm the search API once to reduce cold-start latency
  useEffect(() => {
    if (!SEARCH_PUBLIC_API) return
    const warm = async () => {
      try {
        await fetch(`${SEARCH_PUBLIC_API}/public/search?q=warm`, { cache: 'no-store' })
      } catch {
        /* ignore */
      }
    }
    warm()
  }, [])

  const renderCard = (item: SearchResult) => {
    const stack = guessStack(item)
    const professionLabel =
      item.displayProfession ||
      item.profession ||
      STACK_LABELS[stack] ||
      'Professional'
    const ratingLabel = item.rating ? item.rating.toFixed(1) : null
    const starCount = item.rating ? Math.max(1, Math.min(5, Math.round(Number(item.rating)))) : 0
    const specialties = (item.specialties || []).filter(Boolean)
    const profileSlug = (item.slug || item.name || 'provider').toLowerCase().replace(/[^a-z0-9]+/g, '')
    const profileUrl = `/profile/${profileSlug}`
    return (
      <div key={(item.userId || item.slug || profileSlug) + stack} className="clients-search-card">
        <div
          className="clients-search-photo"
          role="button"
          tabIndex={0}
          onClick={() => navigate(profileUrl, { state: { provider: item } })}
          onKeyDown={(e) => { if (e.key === 'Enter') navigate(profileUrl, { state: { provider: item } }) }}
        >
          <img src={item.image || '/logo.png'} alt={item.name} />
          <span className="clients-search-pill">{professionLabel}</span>
        </div>
        <div className="clients-search-body">
          <div className="clients-search-row">
            <div>
              <h3 className="provider-name-line">
                <button
                  type="button"
                  className="link-plain"
                  onClick={() => navigate(profileUrl, { state: { provider: item } })}
                >
                  {item.name}
                </button>
                <LicenseVerifiedBadge item={item} />
              </h3>
            </div>
         </div>
          {ratingLabel && (
            <button
              type="button"
              className="clients-search-rating"
              onClick={() => openReviews(item)}
            >
              <div className="rating-row">
                <span className="value">{ratingLabel}</span>
                <span aria-hidden>
                  {'★'.repeat(starCount || 0)}
                </span>
              </div>
              <div className="reviews-count">{reviewCountLabel(item.ratingCount)}</div>
            </button>
          )}
         <div className="clients-search-meta-row">
            <p className="muted small">
              {specialties.length ? specialties.slice(0, 3).join(' · ') : '—'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  const renderPlaceholderCard = (key: string | number) => (
    <div key={key} className="clients-search-card placeholder">
      <div className="clients-search-photo skeleton" />
      <div className="clients-search-body">
        <div className="clients-search-row">
          <div className="skeleton skeleton-text" style={{ width: '60%' }} />
          <div className="skeleton skeleton-text" style={{ width: 50 }} />
        </div>
        <div className="skeleton skeleton-text" style={{ width: '90%', marginTop: 8 }} />
        <div className="skeleton skeleton-text" style={{ width: '70%', marginTop: 6 }} />
      </div>
    </div>
  )

  const scrollShowcase = (stack: string, dir: number) => {
    const el = showcaseRefs.current[stack]
    if (!el) return
    const amount = el.clientWidth * 0.8
    el.scrollBy({ left: dir * amount, behavior: 'smooth' })
  }
  const professionLabel = useMemo(
    () => (profession ? STACK_LABELS[profession as ProfessionKey] || '' : ''),
    [profession]
  )

  const handleShowcaseClick = (stackKey: string) => {
    const stack = stackKey as ProfessionKey
    setProfession(stack)
    setQuery('')
    setSuggestions([])
    setSuggestionsOpen(false)
    setSearchFocused(false)
    setError(null)
    runSearch('', stack)
  }

  return (
    <div className="container" style={{ paddingTop: '1.8rem', paddingBottom: '2.4rem' }}>
      <section className="clients-search">
        <div className="clients-search-head">
          <h2>Find a professional</h2>
        </div>
        <form
          className="clients-search-form"
          onSubmit={(e) => {
            e.preventDefault()
            runSearch()
          }}
        >
          <div className="clients-search-row">
            <div className="clients-search-field">
              <Search aria-hidden />
              <input
                type="search"
                placeholder={`Search: ${rotatingPlaceholder || 'tutor, nurse, legal'}`}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                  setShowProfessionSelect(true)
                }}
                onFocus={() => {
                  setSearchFocused(true)
                  suggestions.length && setSuggestionsOpen(true)
                }}
                onBlur={() => {
                  setSearchFocused(false)
                  setTimeout(() => setSuggestionsOpen(false), 120)
                }}
              />
            </div>
            {showProfessionSelect && (
              <div className="clients-search-field select">
                <BriefcaseBusiness aria-hidden />
                <select value={profession} onChange={(e) => setProfession(e.target.value as ProfessionKey | '')}>
                  {STACK_OPTIONS.map((opt) => (
                    <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            )}
            <button className="btn btn-primary" type="submit" disabled={loading}>
              {loading ? 'Searching…' : 'Search'}
            </button>
            <button
              className="btn btn-outline clients-ai-search-trigger"
              type="button"
              onClick={() => navigate('/ai-search')}
            >
              <Sparkles size={16} aria-hidden />
              AI Search
            </button>
          </div>

        </form>

        {/* Suggestions below the bar, push content down */}
        {suggestionsOpen && (
          <div className="search-suggestions" role="listbox" aria-label="Search suggestions">
            <div className="search-suggestions-header">
              <p className="eyebrow">Search suggestions</p>
              {suggestionsLoading && <p className="muted tiny">Loading…</p>}
            </div>
            {!suggestionsLoading && (
              <div className="suggestion-grid">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="suggestion-chip"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setQuery(s)
                      setSuggestionsOpen(false)
                      setSuggestions([])
                      setProfession('')
                      setSearchFocused(false)
                      runSearch(s, profession)
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {professionLabel ? <p className="search-selected-eyebrow">{professionLabel}</p> : null}

        {showFilters && (
          <div className={`clients-filters ${filtersOpen ? 'open' : ''}`.trim()}>
            <div className="clients-filters-header">
              <button
                type="button"
                className="clients-filters-toggle"
                onClick={() => setFiltersOpen((open) => !open)}
                aria-expanded={filtersOpen}
                aria-controls="clients-filters-body"
              >
                <Filter size={16} aria-hidden />
                Filters{activeFilterCount ? ` (${activeFilterCount})` : ''}
              </button>
              {filtersActive ? (
                <button type="button" className="clients-filters-clear-inline" onClick={clearFilters}>
                  Clear
                </button>
              ) : null}
            </div>
            <div className="clients-filters-body" id="clients-filters-body">
              <div className="clients-filter-row">
                <label className="clients-filter-group">
                  <span className="clients-filter-label">Role</span>
                  <select
                    className="clients-filter-input"
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                  >
                    <option value="">All roles</option>
                    {roleOptions.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="clients-filter-group">
                  <span className="clients-filter-label">Region / State / Province</span>
                  <select
                    className="clients-filter-input"
                    value={stateFilter}
                    onChange={(e) => setStateFilter(e.target.value)}
                  >
                    <option value="">All regions</option>
                    {regionOptions.map((region) => (
                      <option key={region} value={region}>
                        {region}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="clients-filter-group">
                  <span className="clients-filter-label">Rating</span>
                  <select
                    className="clients-filter-input"
                    value={ratingFilter}
                    onChange={(e) => setRatingFilter(e.target.value)}
                  >
                    <option value="any">Any rating</option>
                    <option value="4.5">4.5+</option>
                    <option value="4.0">4.0+</option>
                  </select>
                </label>
                <label className="clients-filter-group sort">
                  <span className="clients-filter-label">Sort</span>
                  <select
                    className="clients-filter-input"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="recommended">Recommended</option>
                    <option value="rating">Highest rated</option>
                    <option value="reviews">Most reviews</option>
                    <option value="name-asc">Name A-Z</option>
                    <option value="name-desc">Name Z-A</option>
                  </select>
                </label>
              </div>
              {filtersActive ? (
                <div className="clients-filter-chips">
                  {roleFilter ? <span className="clients-filter-chip">Role: {roleFilter}</span> : null}
                  {stateFilter ? <span className="clients-filter-chip">Region: {stateFilter}</span> : null}
                  {ratingFilter !== 'any' ? (
                    <span className="clients-filter-chip">Rating: {ratingFilter}+</span>
                  ) : null}
                  <button type="button" className="clients-filter-clear" onClick={clearFilters}>
                    Clear filters
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        )}

        {error && <div className="banner error" role="alert" style={{ marginTop: 12 }}>{error}</div>}
        {loading && <p className="muted" style={{ marginTop: 12 }}>Searching…</p>}

        {!loading && results.length > 0 && filteredResults.length === 0 && !error && (
          <p className="muted" style={{ marginTop: 12 }}>No professionals match your filters.</p>
        )}
        {filteredResults.length > 0 && <div className="clients-search-results">{filteredResults.map(renderCard)}</div>}
      </section>

      {!loading && filteredResults.length === 0 && (
        <section className="clients-showcase">
          {showcaseError && <div className="banner error" role="alert" style={{ marginTop: 8 }}>{showcaseError}</div>}
          {(showcaseLoading && showcaseError == null) && !Object.values(showcase).some((arr) => arr.length) && (
            <p className="muted" style={{ marginTop: 8 }}>Loading professionals…</p>
          )}
          {Object.entries(STACK_LABELS)
              .map(([stackKey, label]) => {
                const items = showcase[stackKey as ProfessionKey] || []
                const reviewedCount = items.reduce(
                  (count, item) => count + ((Number(item?.ratingCount) || 0) > 0 ? 1 : 0),
                  0
                )
                const totalReviews = items.reduce(
                  (sum, item) => sum + Math.max(0, Number(item?.ratingCount) || 0),
                  0
                )
                return {
                  stackKey,
                  label,
                  count: items.length,
                  qualifiedBookingCount: showcaseBookingCounts[stackKey as ProfessionKey] || 0,
                  reviewedCount,
                  totalReviews,
                }
              })
              .filter((row) => row.count > 0)
              .sort((a, b) => {
                const bookingDiff = b.qualifiedBookingCount - a.qualifiedBookingCount
                if (bookingDiff !== 0) return bookingDiff
                const reviewedDiff = b.reviewedCount - a.reviewedCount
                if (reviewedDiff !== 0) return reviewedDiff
                return b.totalReviews - a.totalReviews
              })
              .map(({ stackKey, label }) => {
                const allItems = showcase[stackKey as ProfessionKey] || []
                const items = allItems.slice(0, MAX_SHOWCASE_CARDS)
                const showSeeMore = allItems.length > MAX_SHOWCASE_CARDS
                const placeholders = items.length ? [] : Array.from({ length: 6 }, (_, i) => i)
                return (
                  <div key={stackKey} className="clients-showcase-row">
                    <button
                      type="button"
                      className="clients-showcase-title-link"
                      onClick={() => handleShowcaseClick(stackKey)}
                    >
                      <h4 className="clients-showcase-title">{label}</h4>
                      <ArrowRight className="clients-showcase-title-arrow" aria-hidden="true" />
                    </button>
                    <div className="clients-showcase-shell">
                      <button
                        type="button"
                        className="clients-showcase-nav"
                        aria-label={`Scroll ${label} left`}
                        onClick={() => scrollShowcase(stackKey, -1)}
                      >
                        ‹
                      </button>
                      <div
                        className="clients-showcase-track"
                        ref={(el) => {
                          showcaseRefs.current[stackKey] = el
                        }}
                      >
                        {(items.length ? items : placeholders).map((it, idx) =>
                          items.length ? renderCard(it as SearchResult) : renderPlaceholderCard(`ph-${stackKey}-${idx}`)
                        )}
                        {showSeeMore ? (
                          <button
                            type="button"
                            className="clients-search-card cta-card"
                            onClick={() => handleShowcaseClick(stackKey)}
                          >
                            <div className="showcase-cta">
                              <div className="showcase-cta-icon" aria-hidden="true">
                                <ArrowRight />
                              </div>
                              <div className="showcase-cta-title">See more professionals</div>
                              <div className="showcase-cta-sub">View all {label}</div>
                            </div>
                          </button>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        className="clients-showcase-nav"
                        aria-label={`Scroll ${label} right`}
                        onClick={() => scrollShowcase(stackKey, 1)}
                      >
                        ›
                      </button>
                    </div>
                  </div>
                )
              })}
        </section>
      )}

      {/* HERO / LANDING */}
      <section className="hero-card hero-card--clients">
        <div className="hero-head">
          <img src={heroLogo} alt="EUCA Match" className="hero-logo" />
          <div>
            <h1>For Clients</h1>
          </div>
        </div>
        <div className="clients-two-col">
          <div className="clients-card">
            <h3>How it works</h3>
            <ul className="clients-list">
              <li>
                <Search aria-hidden="true" />
                <span>Start a search</span>
              </li>
              <li>
                <Users aria-hidden="true" />
                <span>Select a professional</span>
              </li>
              <li>
                <Bell aria-hidden="true" />
                <span>
                  Send your request —{' '}
                  <span className="promo-price-stack">
                    <span className="new-price">FREE</span>
                  </span>
                </span>
              </li>
            </ul>
          </div>
          <div className="clients-card">
            <h3>After you send</h3>
            <ul className="clients-list">
              <li>
                <ShieldCheck aria-hidden="true" />
                <span>Verified professionals receive your request</span>
              </li>
              <li>
                <CheckCircle2 aria-hidden="true" />
                <span>Arrange details directly with professionals</span>
              </li>
              <li>
                <Star aria-hidden="true" />
                <span>Leave a review after your appointment</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {reviewsPanel.provider && (
        <div className="reviews-overlay" role="dialog" aria-modal="true" onClick={closeReviews}>
          <div className={`reviews-drawer ${reviewsPanel.loading ? 'is-loading' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="reviews-head">
              <button className="reviews-close" type="button" aria-label="Close reviews" onClick={closeReviews}>
                ✕
              </button>
            </div>
            <div className="reviews-scroll" ref={reviewsScrollRef}>
              <div className="reviews-title">{`What ${providerFirstName(reviewsPanel.provider.name)}'s customers say`}</div>
              <div className="reviews-summary">
                <div className="reviews-brand">
                  <img
                    src={reviewsPanel.provider.image || '/logo.png'}
                    alt={reviewsPanel.provider.name}
                    className="reviews-provider-photo"
                  />
                  <div className="reviews-provider-name">{reviewsPanel.provider.name}</div>
                </div>
                <div className="reviews-avg">
                  <div className="reviews-avg-score">
                    {Number(reviewsPanel.summary?.ratingAverage ?? Number(reviewsPanel.provider.rating || 0)).toFixed(1)}
                  </div>
                  <div className="reviews-avg-stars" aria-hidden="true">
                    {renderStars(reviewsPanel.summary?.ratingAverage ?? reviewsPanel.provider.rating)}
                  </div>
                  <div className="reviews-avg-count">
                    {reviewCountLabel(reviewsPanel.summary?.ratingCount ?? reviewsPanel.provider.ratingCount ?? 0)}
                  </div>
                </div>
                <button
                  className="reviews-cta-primary"
                  type="button"
                  onClick={() => {
                    const url = directBookingUrl(reviewsPanel.provider)
                    navigate(url)
                  }}
                >
                  Match Now
                </button>
              </div>
              <div className="reviews-body">
                {reviewsPanel.loading && <div className="muted">Loading reviews…</div>}
                {reviewsPanel.error && !reviewsPanel.loading && <div className="banner error">{reviewsPanel.error}</div>}
                {!reviewsPanel.loading && !reviewsPanel.error && !reviewsPanel.reviews.length ? (
                  <div className="muted">No reviews yet.</div>
                ) : null}
                {!reviewsPanel.loading &&
                  !reviewsPanel.error &&
                  reviewsPanel.reviews.slice(0, reviewsVisibleCount).map((rev, idx) => (
                    <div key={rev.reviewId || rev.id || idx} className="review-item">
                      <div className="review-head">
                        <div className="reviewer-badge" aria-hidden="true">
                          {(rev.reviewerFirstName || 'Client').slice(0, 1).toUpperCase()}
                        </div>
                        <div className="reviewer-meta">
                          <strong>{rev.reviewerFirstName || 'Client'}</strong>
                          <span className="hint">
                            {formatReviewDate(rev.slotISO, rev.createdAt)}
                            {rev.source ? (
                              <>
                                {' - '}
                                <em>{rev.source}</em>
                              </>
                            ) : null}
                          </span>
                        </div>
                        <div className="review-rating">
                          {renderStars(rev.rating)}
                        </div>
                      </div>
                      {rev.comment ? <p className="review-comment">{rev.comment}</p> : null}
                    </div>
                  ))}
                {reviewsVisibleCount < reviewsPanel.reviews.length && !reviewsPanel.loading && !reviewsPanel.error ? (
                  <button
                    className="reviews-cta-secondary"
                    type="button"
                    onClick={loadMoreReviews}
                    disabled={reviewsVisibleCount >= reviewsPanel.reviews.length}
                  >
                    Load more reviews
                  </button>
                ) : null}
                <div ref={reviewsSentinelRef} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
